import React from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import BerlinBeachVibes from './BerlinBeachVibes';

const Productlab: React.FC = () => {
  const stats = [
    { value: "3 Days", label: "Sept 15–17, 2026", color: "text-emerald-600", bg: "bg-emerald-50" },
    { value: "300+", label: "Participants", color: "text-primary-600", bg: "bg-primary-50" },
    { value: "12", label: "Featured Speakers", color: "text-blue-600", bg: "bg-blue-50" },
    { value: "5", label: "Hands-on Workshops", color: "text-indigo-600", bg: "bg-indigo-50" }
  ];

  const days = [
    {
      title: "Day 1 · Leaders Lab",
      text: "A full-day working summit for heads of product, with teaching sessions and peer roundtables, capped at 60 leaders and ending in a concrete 90-day plan.",
      dot: "bg-emerald-600"
    },
    {
      title: "Day 2 · Craft & AI Labs",
      text: "A practice day of small-group, hands-on workshops with practitioners on building decisions, prioritisation and product operations in the AI era.",
      dot: "bg-blue-600"
    },
    {
      title: "Day 3 · Conference Day",
      text: "Keynotes, talks, fireside chats, deep dives and roundtables at CIC Berlin, with speakers from companies such as Google, Zalando, Delivery Hero, Mercedes-Benz and Miro.",
      dot: "bg-indigo-600"
    }
  ];

  const photos = [
    { src: "/personal_images/productlab/organizing-team.jpg", alt: "Productlab Conference 2026 organizing team", className: "md:col-span-2" },
    { src: "/personal_images/productlab/crowd-selfie.jpg", alt: "Conference Day audience at Productlab Conference 2026", className: "" },
    { src: "/personal_images/productlab/with-speaker.jpg", alt: "Ahmed with a speaker at Productlab Conference 2026", className: "" }
  ];

  return (
    <section id="community" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Community & Events
          </h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto rounded-full"></div>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Building communities in Berlin, on the sand and in product
          </p>
        </motion.div>

        <BerlinBeachVibes />

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto mt-12"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-gray-900 via-emerald-900 to-emerald-800 p-8">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-1 text-white">Organizing Team · Productlab Conference 2026</h3>
                <a
                  href="https://productlab.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-200 hover:text-white font-medium transition-colors duration-300"
                >
                  Productlab Community · Berlin
                </a>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Header Info */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8 pb-6 border-b border-gray-200">
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-2 text-primary-600" />
                  <span className="font-medium">Berlin · The Social Hub & CIC Berlin</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-2 text-primary-600" />
                  <span className="font-medium">September 15–17, 2026</span>
                </div>
                <div className="flex items-center">
                  <UserGroupIcon className="h-4 w-4 mr-2 text-primary-600" />
                  <span className="font-medium">Organizing Team</span>
                </div>
                <div className="flex items-center">
                  <svg className="h-4 w-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0 0V3" />
                  </svg>
                  <a
                    href="https://productlab.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary-600 hover:text-primary-700 transition-colors duration-300"
                  >
                    productlab.app
                  </a>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mb-8">
                I was part of the organizing team behind Productlab Conference 2026, a three-day gathering in Berlin
                for in-house product teams working through what AI actually changes about how products are built and led.
                The program was aimed at senior practitioners, from senior PMs to heads of product and CPOs.
              </p>

              {/* Highlights */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {stats.map((stat) => (
                  <div key={stat.value} className={`text-center p-4 ${stat.bg} rounded-xl`}>
                    <div className={`text-xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Program */}
              <div className="mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-6">The Program</h4>
                <div className="space-y-4">
                  {days.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: (index + 1) * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-4 p-4 bg-gradient-to-r from-gray-50 to-emerald-50 rounded-lg"
                    >
                      <div className={`w-2 h-2 rounded-full ${item.dot} mt-3 flex-shrink-0`}></div>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>{item.title}:</strong> {item.text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Photos */}
              <div className="mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-6">Moments</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {photos.map((photo, index) => (
                    <motion.div
                      key={photo.src}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className={`overflow-hidden rounded-xl shadow-md ${photo.className}`}
                    >
                      <img
                        src={photo.src}
                        alt={photo.alt}
                        loading="lazy"
                        className="w-full h-72 object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Focus Areas */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Topics</h4>
                <div className="flex flex-wrap gap-3">
                  {["AI-Native Product Management", "Product Leadership", "Product Operations", "Team Structure", "Career Growth", "Community"].map((topic, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="px-4 py-2 bg-gradient-to-r from-emerald-100 to-emerald-100 text-emerald-800 text-sm font-medium rounded-full border border-emerald-200 hover:shadow-md transition-shadow"
                    >
                      {topic}
                    </motion.span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Productlab;
